import { clsx } from 'clsx';
import { Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export type BreadcrumbItem = {
  title: string;
  link?: string;
};

type BreadcrumbsProps = {
  title?: string;
  items?: BreadcrumbItem[];
  hasBack?: boolean;
  className?: string;
  /**
   * The **one-line** header the Member/Staff List comp draws instead:
   * `Staff List │ Dashboard > Staff List` — title, a short vertical rule, then the
   * trail to its right.
   *
   * Opt-in rather than the default, because the stacked arrangement below is the
   * `Partners - List` comp's and flipping it would move every screen already built
   * to that one. Colours are this comp's own: a `navy` title, `crumb-rule` for the
   * rule *and* the `>` between crumbs, `crumb` for an inactive crumb and
   * `detail-link` for the one you are on — a quieter, darker trail than the
   * stacked variant's `detail-sub`/`indigo` pair, which is what this comp shows.
   *
   * Its geometry is now measured rather than guessed, off the two consumer-portal
   * comps that draw it — `active subscription - List - 0 - 0 - 1` (2.3×) and
   * `Platform Invoices - List - 0 - 0 - 1` (1.15×) — which agree to within a pixel
   * on both figures: the rule is **28px** tall, not 24, and the crumbs sit **20px**
   * apart, not 12. That is the same 20px as the `gap-x-5` between the title and the
   * rule, so the whole header is on one rhythm.
   *
   * The two comps do disagree on the rule's *colour* — the Subscription List's
   * sampled `crumb-rule` (#92969A) and the Platform Invoices' a lighter #C5C6CB —
   * so the shared `crumb-rule` stands rather than one screen's reading becoming the
   * component's.
   */
  inline?: boolean;
  /**
   * Colour classes for the crumb you are on — the trailing one — replacing this
   * component's default for whichever variant is drawn (`indigo` stacked,
   * `detail-link` inline).
   *
   * A prop rather than a new default because the two portals now disagree on it:
   * the provider portal asked for `primary` (#21376F) on every one of its pages —
   * the same hex as its selected tab, so the two pieces of "where am I" chrome
   * match — while the consumer portal's screens keep the colours sampled off their
   * own comps. `RouteBreadcrumbs` in `apps/data-provider` is the single place that
   * passes it, so the whole portal moves together.
   */
  activeCrumbClassName?: string;
};

/**
 * Page header: the crumb trail on its own line at the top, the page title under
 * it — or, with `inline`, the two on one line separated by a rule.
 *
 * The trail used to sit on the *right of the title row*, sharing one line with
 * it. That reads as a second, competing header rather than as "where am I" — and
 * on a narrow viewport it wrapped under a long title, landing below the thing it
 * is supposed to introduce. Stacking it above is both the conventional order and
 * stable at every width.
 *
 * ### Type and colour
 *
 * Both are the `Partners - List - 0 - 0 - 3` comp's, sampled pixel-by-pixel: a
 * 15px trail in `detail-sub` with the trailing crumb in `indigo` and the `>`
 * between them in `crumb-rule`, then a 26px bold title in `dash-ink`, 24px under
 * it. The trailing crumb's colour is the one piece a caller can override — see
 * `activeCrumbClassName`. What was here before came from the Vuexy theme this app replaced — a
 * `text-xl` slate-900 title over a cyan active crumb, and cyan appears in no GDX
 * design at all.
 */
export function Breadcrumbs({
  title,
  items,
  hasBack,
  className,
  inline,
  activeCrumbClassName,
}: BreadcrumbsProps) {
  const navigate = useNavigate();

  const backButton = hasBack ? (
    <button
      type="button"
      aria-label="Go back"
      onClick={() => navigate(-1)}
      className="text-ds-text-secondary hover:text-gdx-dash-ink dark:text-slate-400 dark:hover:text-slate-200"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M19 12H5M12 19l-7-7 7-7" />
      </svg>
    </button>
  ) : null;

  if (inline) {
    return (
      <div className={clsx('flex flex-wrap items-center gap-x-5 gap-y-2', className)}>
        {/* A trail with no title still gets its back button — the shell's route header. */}
        {!title && backButton}
        {title ? (
          <h2 className="flex items-center gap-2 text-xl font-semibold leading-tight text-gdx-navy dark:text-white">
            {backButton}
            {title}
          </h2>
        ) : null}

        {/* The rule between the two. Decorative, so it is not announced. */}
        {title && items?.length ? (
          <span aria-hidden className="h-7 w-px shrink-0 bg-gdx-crumb-rule dark:bg-slate-700" />
        ) : null}

        {items?.length ? (
          <ol className="flex flex-wrap items-center gap-5 text-[13px]">
            {items.map((item, index) => {
              const isLast = index === items.length - 1;
              return (
                <Fragment key={index}>
                  {index > 0 && (
                    <span aria-hidden className="text-gdx-crumb-rule dark:text-slate-600">
                      &gt;
                    </span>
                  )}
                  <li
                    className={clsx(
                      isLast
                        ? (activeCrumbClassName ?? 'text-gdx-detail-link dark:text-indigo-300')
                        : 'text-gdx-crumb dark:text-slate-400'
                    )}
                  >
                    {item.link ? <Link to={item.link}>{item.title}</Link> : item.title}
                  </li>
                </Fragment>
              );
            })}
          </ol>
        ) : null}
      </div>
    );
  }

  return (
    <div className={clsx('flex flex-col gap-6', className)}>
      {items?.length ? (
        <ol className="flex flex-wrap items-center gap-2.5 text-[15px] text-ds-text-secondary dark:text-slate-400">
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            return (
              <Fragment key={index}>
                {/* `>` per the design. `aria-hidden` because a separator read
                    aloud between every crumb is noise — the list markup already
                    conveys the structure. */}
                {index > 0 && (
                  <span aria-hidden className="text-gdx-crumb-rule dark:text-slate-600">
                    &gt;
                  </span>
                )}
                <li
                  className={clsx(
                    isLast
                      ? (activeCrumbClassName ?? 'text-ds-text-accent dark:text-indigo-300')
                      : 'text-ds-text-secondary dark:text-slate-400'
                  )}
                >
                  {item.link ? <Link to={item.link}>{item.title}</Link> : item.title}
                </li>
              </Fragment>
            );
          })}
        </ol>
      ) : null}

      {title ? (
        <h2 className="flex items-center gap-2 text-[26px] font-bold leading-tight text-gdx-dash-ink dark:text-white">
          {backButton}
          {title}
        </h2>
      ) : null}
    </div>
  );
}
